import { Suspense, lazy, useEffect, useState } from 'react';
import { supabase, Profile } from './lib/supabase';
import { Session } from '@supabase/supabase-js';
import { LoginScreen } from './screens/LoginScreen';
import { SignupScreen } from './screens/SignupScreen';
import { AcceptInviteScreen } from './screens/AcceptInviteScreen';
import { UpdatePasswordScreen } from './screens/UpdatePasswordScreen';
import { HomeScreen } from './screens/HomeScreen';
import { Sidebar, Screen, PageSkeleton, DeliveryDetailProvider, DemoBanner } from './components/ui';

const FailedDeliversScreen = lazy(() => import('./screens/FailedDeliversScreen').then(m => ({ default: m.FailedDeliversScreen })));
const DifotScreen = lazy(() => import('./screens/DifotScreen').then(m => ({ default: m.DifotScreen })));
const DriverPerformanceScreen = lazy(() => import('./screens/DriverPerformanceScreen').then(m => ({ default: m.DriverPerformanceScreen })));
const FeedbackScreen = lazy(() => import('./screens/FeedbackScreen').then(m => ({ default: m.FeedbackScreen })));
const DeliveryCostScreen = lazy(() => import('./screens/DeliveryCostScreen').then(m => ({ default: m.DeliveryCostScreen })));
const BoardReportScreen = lazy(() => import('./screens/BoardReportScreen').then(m => ({ default: m.BoardReportScreen })));
const UsersScreen = lazy(() => import('./screens/UsersScreen').then(m => ({ default: m.UsersScreen })));

const SCREEN_TO_PATH: Record<Screen, string> = {
  dashboard: '/',
  failed: '/failed-deliveries',
  difot: '/difot',
  driver_performance: '/driver-performance',
  feedback: '/feedback',
  delivery_cost: '/delivery-cost',
  board_report: '/board-report',
  users: '/users',
};

const PATH_TO_SCREEN: Record<string, Screen> = Object.entries(SCREEN_TO_PATH)
  .reduce((acc, [s, p]) => { acc[p] = s as Screen; return acc; }, {} as Record<string, Screen>);

const DEFAULT_SCREEN: Screen = 'dashboard';

function screenFromLocation(): Screen {
  if (typeof window === 'undefined') return DEFAULT_SCREEN;
  const match = PATH_TO_SCREEN[window.location.pathname];
  if (!match) return DEFAULT_SCREEN;
  return match;
}

const ACCEPT_INVITE_PATH = '/accept-invite';
const RESET_PASSWORD_PATH = '/reset-password';
const SIGNUP_PATH = '/signup';

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [screen, setScreenState] = useState<Screen>(screenFromLocation);
  const [pathname, setPathname] = useState<string>(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );

  function setScreen(next: Screen) {
    setScreenState(next);
    const path = SCREEN_TO_PATH[next];
    if (path && window.location.pathname !== path) {
      window.history.pushState({ screen: next }, '', path);
    }
  }

  useEffect(() => {
    const onPop = () => {
      setScreenState(screenFromLocation());
      setPathname(window.location.pathname);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setBootstrapped(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) { setProfile(null); return; }
    let cancelled = false;
    supabase
      .from('profiles')
      .select('id, email, full_name, role, is_admin')
      .eq('id', session.user.id)
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setProfile(data as Profile | null); });
    return () => { cancelled = true; };
  }, [session]);

  async function handleLogout() {
    await supabase.auth.signOut();
    setScreen(DEFAULT_SCREEN);
  }

  // Public routes that manage their own auth via the email-link token.
  if (pathname === ACCEPT_INVITE_PATH) {
    return <AcceptInviteScreen />;
  }
  if (pathname === RESET_PASSWORD_PATH) {
    return <UpdatePasswordScreen />;
  }

  if (!bootstrapped) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--ws-bone)',
      }}>
        <svg className="ws-spin" width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="var(--ws-blue)" strokeWidth="2" strokeDasharray="40" strokeDashoffset="20" />
        </svg>
      </div>
    );
  }

  if (!session) {
    if (pathname === SIGNUP_PATH) return <SignupScreen />;
    return <LoginScreen />;
  }

  const sidebarUser = {
    name: profile?.full_name || session.user.email || 'Signed in',
    role: profile?.is_admin ? 'Administrator' : 'Viewer',
    initials: initialsOf(profile?.full_name || session.user.email || '?'),
  };

  const isAdmin = profile?.is_admin === true;
  const effectiveScreen: Screen = screen === 'board_report' && !isAdmin ? DEFAULT_SCREEN : screen;

  return (
    <DeliveryDetailProvider>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <DemoBanner />
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Sidebar
          current={effectiveScreen}
          onNav={(s) => setScreen(s)}
          onLogout={handleLogout}
          user={sidebarUser}
          isAdmin={isAdmin}
        />
        <main style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Suspense fallback={<PageSkeleton />}>
            {effectiveScreen === 'dashboard' && <HomeScreen onNav={setScreen} isAdmin={isAdmin} />}
            {effectiveScreen === 'failed' && <FailedDeliversScreen />}
            {effectiveScreen === 'difot' && <DifotScreen />}
            {effectiveScreen === 'driver_performance' && <DriverPerformanceScreen />}
            {effectiveScreen === 'feedback' && <FeedbackScreen />}
            {effectiveScreen === 'delivery_cost' && <DeliveryCostScreen />}
            {effectiveScreen === 'board_report' && isAdmin && <BoardReportScreen />}
            {effectiveScreen === 'users' && profile && (
              <UsersScreen currentUser={profile} />
            )}
          </Suspense>
        </main>
        </div>
      </div>
    </DeliveryDetailProvider>
  );
}

function initialsOf(name: string) {
  const parts = name.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
